import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
import { useSelector } from "react-redux";

import {
  AlertBannerContext,
  API_RESPONSE_CODE,
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  HandledGraphQLMutationError
} from "@kivunova/kivufrontendcommon";

import { fetchGraphQL } from "../../Graphql/utils";

import {
  listBillingCyclesQuery,
  getBillingCycleQuery
} from "../../Graphql/querries/billingCycleQuery";

import {
  ListBillingCyclesResponse,
  GetBillingCycleResponse,
  ListBillingCyclesInput,
  GetBillingCycleInput
} from "../../Graphql/types/billingCycle";

import { RootState } from "../../store/store";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

export const useGetBillingCycleList = () => {
  const { setAlert } = useContext(AlertBannerContext);

  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<
    ListBillingCyclesResponse,
    Error,
    ListBillingCyclesInput | void
  >({
    mutationKey: ["listBillingCycles"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching billing cycle list",
        buildLogContext(
          "membership",
          "list-billing-cycles",
          userId,
          companyId,
          storeId
        )
      );

      const response: ListBillingCyclesResponse = await fetchGraphQL(
        listBillingCyclesQuery,
        { input: input ?? {} },
        setAlert
      );

      const listResult = response?.listBillingCycles;

      // ❌ Unexpected response shape
      if (!listResult) {
        captureAppException(
          "List billing cycles failed: missing listBillingCycles",
          new Error("List billing cycles failed: missing listBillingCycles"),
          buildLogContext(
            "membership",
            "list-billing-cycles",
            userId,
            companyId,
            storeId
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });

        throw new HandledGraphQLMutationError(
          "List billing cycles failed: missing listBillingCycles"
        );
      }

      // ⚠️ Expected API/business failure
      if (listResult.code !== API_RESPONSE_CODE.OK) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "List billing cycles rejected by API",
          buildLogContext(
            "membership",
            "list-billing-cycles",
            userId,
            companyId,
            storeId,
            { apiCode: String(listResult.code) }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: listResult.code as unknown as ALERT_BANNER_CODE
        });
        throw new HandledGraphQLMutationError(
          `List billing cycles rejected by API: ${listResult.code}`,
          listResult.code
        );
      }

      // ✅ Success
      sendAppLog(
        LOG_LEVEL.INFO,
        "Billing cycle list fetched successfully",
        buildLogContext(
          "membership",
          "list-billing-cycles",
          userId,
          companyId,
          storeId,
          {
            billingCycleCount: Array.isArray(listResult.billingCycleList)
              ? listResult.billingCycleList.length
              : 0
          }
        )
      );

      return response;
    }
  });
};

export const useGetBillingCycleDetail = () => {
  const { setAlert } = useContext(AlertBannerContext);

  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<GetBillingCycleResponse, Error, GetBillingCycleInput>({
    mutationKey: ["getBillingCycle"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching billing cycle detail",
        buildLogContext(
          "membership",
          "get-billing-cycle",
          userId,
          companyId,
          storeId,
          {
            billingCycleId: String(input.billingCycleId)
          }
        )
      );

      const response: GetBillingCycleResponse = await fetchGraphQL(
        getBillingCycleQuery,
        { input },
        setAlert
      );

      const getResult = response?.getBillingCycle;

      // ❌ Unexpected response shape
      if (!getResult) {
        captureAppException(
          "Get billing cycle failed: missing getBillingCycle",
          new Error("Get billing cycle failed: missing getBillingCycle"),
          buildLogContext(
            "membership",
            "get-billing-cycle",
            userId,
            companyId,
            storeId,
            {
              billingCycleId: String(input.billingCycleId)
            }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });

        throw new HandledGraphQLMutationError(
          "Get billing cycle failed: missing getBillingCycle"
        );
      }

      // ⚠️ Expected API/business failure
      if (getResult.code !== API_RESPONSE_CODE.OK) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Get billing cycle detail rejected by API",
          buildLogContext(
            "membership",
            "get-billing-cycle",
            userId,
            companyId,
            storeId,
            {
              billingCycleId: String(input.billingCycleId),
              apiCode: String(getResult.code)
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: getResult.code as unknown as ALERT_BANNER_CODE
        });
        throw new HandledGraphQLMutationError(
          `Get billing cycle detail rejected by API: ${getResult.code}`,
          getResult.code
        );
      }

      // ✅ Success
      sendAppLog(
        LOG_LEVEL.INFO,
        "Billing cycle detail fetched successfully",
        buildLogContext(
          "membership",
          "get-billing-cycle",
          userId,
          companyId,
          storeId,
          {
            billingCycleId: String(input.billingCycleId)
          }
        )
      );

      return response;
    }
  });
};
